import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  Divider,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalClose,
  ModalDialog,
  Stack,
  Textarea,
  Typography,
} from "@mui/joy";
import {
  CheckCircle,
  EnvelopeSimple,
  FilePdf,
  PencilSimple,
  Plus,
  Signature,
  Trash,
} from "@phosphor-icons/react";
import { apiClient, downloadApiBlob } from "../../lib/api";
import { usePermission } from "../../hooks/usePermission";
import { PdfViewerModal } from "../crm/contracts/PdfViewerModal";

interface ScopeItem {
  id?: number;
  description: string;
  quantity: string;
  unit_price: string;
  is_taxable: boolean;
  total?: string;
}
interface DeliveryResult {
  status: string;
  error?: string;
  recipient?: string;
}
interface ChangeOrder {
  id: number;
  job: number;
  document_number: string;
  revision: number;
  request_id: string;
  change_reason: string;
  notes: string;
  terms_and_conditions: string;
  line_items: ScopeItem[];
  change_amount: string;
  subtotal: string;
  tax_amount: string;
  status: string;
  contract: number | null;
  contract_status?: string;
  customer_name: string;
  customer_email: string;
  customer_phone: string;
  signed_document: number | null;
  completion_error: string;
  accepted_at?: string;
  deliveries: {
    id: number;
    results: Record<string, DeliveryResult>;
    created_at: string;
  }[];
}
interface Draft {
  id?: number;
  revision?: number;
  request_id: string;
  change_reason: string;
  notes: string;
  terms_and_conditions: string;
  line_items: ScopeItem[];
}
const blankLine = (): ScopeItem => ({
  description: "",
  quantity: "1",
  unit_price: "0.00",
  is_taxable: false,
});
const dollars = (value: number | string) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(
    Number(value) || 0,
  );
const message = (e: unknown) =>
  e instanceof Error
    ? e.message
    : "This action could not be completed. Please try again.";
const successful = (status: string) =>
  [
    "sent",
    "queued",
    "accepted",
    "scheduled",
    "dispatching",
    "delivered",
    "read",
  ].includes(status);

export function JobChangeOrders({
  jobId,
  baseValue = 0,
}: {
  jobId: number;
  baseValue?: number;
}) {
  const { can } = usePermission();
  const canCreate = can("billing", "create");
  const canEdit = can("billing", "edit");
  const cache = useQueryClient();
  const query = useQuery({
    queryKey: ["job-change-orders", jobId],
    queryFn: async () =>
      (
        await apiClient.getAllPages<ChangeOrder>(
          `/api/job-change-orders/?job=${jobId}`,
        )
      ).data,
    refetchInterval: 15000,
  });
  const rows = query.data || [];
  const signedCopies = rows
    .map((row) => row.signed_document)
    .filter(Boolean)
    .join(",");
  useEffect(() => {
    if (signedCopies)
      void cache.invalidateQueries({ queryKey: ["jobDocs", String(jobId)] });
  }, [cache, jobId, signedCopies]);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [sending, setSending] = useState<ChangeOrder | null>(null);
  const [preview, setPreview] = useState<ChangeOrder | null>(null);
  const [cancelOrder, setCancelOrder] = useState<ChangeOrder | null>(null);
  const [email, setEmail] = useState(true),
    [sms, setSms] = useState(false);
  const [note, setNote] = useState(
    "Please review the scope and price adjustment, then sign to approve.",
  );
  const [recipientEmail, setRecipientEmail] = useState("");
  const [recipientPhone, setRecipientPhone] = useState("");
  const [sendKey, setSendKey] = useState("");
  const [results, setResults] = useState<Record<string, DeliveryResult> | null>(
    null,
  );
  const [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const refresh = async () => {
    await cache.invalidateQueries({ queryKey: ["job-change-orders", jobId] });
    await cache.invalidateQueries({ queryKey: ["jobDocs", String(jobId)] });
  };
  async function act(fn: () => Promise<void>) {
    setBusy(true);
    setError("");
    try {
      await fn();
      await refresh();
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  }
  function newDraft(source?: ChangeOrder) {
    setError("");
    setDraft({
      request_id: crypto.randomUUID(),
      change_reason: source?.change_reason || "",
      notes: source?.notes || "",
      terms_and_conditions: source?.terms_and_conditions || "",
      line_items: source?.line_items.map(
        ({ description, quantity, unit_price, is_taxable }) => ({
          description,
          quantity,
          unit_price,
          is_taxable,
        }),
      ) || [blankLine()],
    });
  }
  const signedTotal = rows
    .filter((r) => r.status === "accepted")
    .reduce((sum, r) => sum + Number(r.change_amount), 0);
  const pending = rows.filter(
    (r) => !["accepted", "cancelled", "void", "draft"].includes(r.status),
  ).length;
  return (
    <Stack spacing={2}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        gap={2}
      >
        <Box>
          <Typography level="h3">Change Orders</Typography>
          <Typography level="body-sm" sx={{ mt: 0.5 }}>
            Agree on scope and price changes before the work begins.
          </Typography>
        </Box>
        <Button
          startDecorator={<Plus />}
          disabled={!canCreate}
          onClick={() => newDraft()}
          sx={{ alignSelf: "flex-start" }}
        >
          New change order
        </Button>
      </Stack>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
          gap: 2,
        }}
      >
        <Card>
          <Typography level="body-sm">Approved changes</Typography>
          <Typography level="h3">{dollars(signedTotal)}</Typography>
          <Typography level="body-xs">
            {rows.filter((r) => r.status === "accepted").length} signed by the
            homeowner
          </Typography>
        </Card>
        <Card>
          <Typography level="body-sm">Awaiting signature</Typography>
          <Typography level="h3">{pending}</Typography>
          <Typography level="body-xs">
            Prepared or sent change orders
          </Typography>
        </Card>
        <Card variant="soft" color="primary">
          <Typography level="body-sm">
            Original job + approved changes
          </Typography>
          <Typography level="h3">
            {dollars(Number(baseValue) + signedTotal)}
          </Typography>
          <Typography level="body-xs">
            Original job value: {dollars(baseValue)}
          </Typography>
        </Card>
      </Box>
      {(error || query.isError) && !draft && !sending && !cancelOrder && (
        <Alert color="danger">
          {error || "Could not load change orders."}
          <Button
            size="sm"
            variant="plain"
            onClick={() => void query.refetch()}
          >
            Refresh
          </Button>
        </Alert>
      )}
      {query.isPending && <Typography>Loading change orders…</Typography>}
      {!query.isPending && !query.isError && !rows.length && (
        <Card sx={{ p: 5, textAlign: "center", alignItems: "center" }}>
          <Signature size={38} />
          <Typography level="title-lg">
            Keep every change clear and approved
          </Typography>
          <Typography sx={{ maxWidth: 560 }}>
            Add the scope, send it by email or text, and collect the homeowner's
            signature. The job owner is notified and the signed copy is saved in
            Documents.
          </Typography>
          <Button disabled={!canCreate} onClick={() => newDraft()}>
            Create the first change order
          </Button>
        </Card>
      )}
      {rows.map((row) => (
        <Card key={row.id} sx={{ gap: 2 }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            gap={1}
          >
            <Box>
              <Stack
                direction="row"
                alignItems="center"
                gap={1}
                flexWrap="wrap"
              >
                <Typography level="title-lg">{row.document_number}</Typography>
                <Chip
                  color={
                    row.status === "accepted"
                      ? "success"
                      : row.status === "cancelled"
                        ? "neutral"
                        : "primary"
                  }
                  size="sm"
                >
                  {row.status === "accepted"
                    ? "Signed & approved"
                    : row.contract_status === "completed"
                      ? "Signed · preparing copy"
                      : row.status === "pending"
                        ? "Ready to send"
                        : row.status.replace(/^./, (c) => c.toUpperCase())}
                </Chip>
              </Stack>
              <Typography sx={{ mt: 1, whiteSpace: "pre-wrap" }}>
                {row.change_reason}
              </Typography>
            </Box>
            <Typography level="h3" sx={{ whiteSpace: "nowrap" }}>
              {Number(row.change_amount) > 0 ? "+" : ""}
              {dollars(row.change_amount)}
            </Typography>
          </Stack>
          <Typography level="body-sm">
            {row.line_items.length} scope item
            {row.line_items.length === 1 ? "" : "s"} · {row.customer_name}
            {row.accepted_at
              ? ` · Signed ${new Date(row.accepted_at).toLocaleDateString()}`
              : ""}
          </Typography>
          {row.completion_error && (
            <Alert
              color="warning"
              endDecorator={
                <Button
                  size="sm"
                  disabled={!canEdit}
                  loading={busy}
                  onClick={() =>
                    void act(async () => {
                      await apiClient.post(
                        `/api/job-change-orders/${row.id}/retry-document/`,
                        {},
                      );
                    })
                  }
                >
                  Retry document
                </Button>
              }
            >
              {row.completion_error}
            </Alert>
          )}
          {row.deliveries[0] && (
            <Typography level="body-xs">
              Latest invitation:{" "}
              {Object.entries(row.deliveries[0].results)
                .map(
                  ([channel, result]) =>
                    `${channel === "sms" ? "Text" : "Email"}: ${channel === "email" && ["accepted", "sent"].includes(result.status) ? "accepted by email provider" : result.status.replaceAll("_", " ")}${result.recipient ? ` · ${result.recipient}` : ""}`,
                )
                .join(" · ")}
            </Typography>
          )}
          <Divider />
          <Stack direction="row" gap={1} flexWrap="wrap">
            <Button
              variant="outlined"
              startDecorator={<FilePdf />}
              onClick={() => setPreview(row)}
            >
              {row.signed_document ? "View signed copy" : "Review PDF"}
            </Button>
            {canEdit && row.status === "draft" && !row.contract && (
              <Button
                variant="plain"
                startDecorator={<PencilSimple />}
                onClick={() => {
                  setError("");
                  setDraft({ ...row });
                }}
              >
                Edit draft
              </Button>
            )}
            {canEdit &&
              !["accepted", "cancelled", "void"].includes(row.status) &&
              row.contract_status !== "completed" && (
                <Button
                  startDecorator={<EnvelopeSimple />}
                  disabled={!canEdit}
                  loading={busy}
                  onClick={() =>
                    void act(async () => {
                      const prepared = row.contract
                        ? row
                        : (
                            await apiClient.post<ChangeOrder>(
                              `/api/job-change-orders/${row.id}/prepare/`,
                              { revision: row.revision },
                            )
                          ).data;
                      setSendKey(crypto.randomUUID());
                      setRecipientEmail(prepared.customer_email || "");
                      setRecipientPhone(prepared.customer_phone || "");
                      setEmail(true);
                      setSms(false);
                      setResults(null);
                      setSending(prepared);
                    })
                  }
                >
                  {row.contract ? "Send for signature" : "Prepare & send"}
                </Button>
              )}
            {row.signed_document && (
              <Button
                variant="plain"
                startDecorator={<CheckCircle />}
                onClick={() =>
                  void act(async () => {
                    await downloadApiBlob(
                      `/api/job-change-orders/${row.id}/pdf/`,
                      `${row.document_number}-signed.pdf`,
                    );
                  })
                }
              >
                Download signed PDF
              </Button>
            )}
            {canCreate && row.status === "cancelled" && (
              <Button variant="plain" onClick={() => newDraft(row)}>
                Create revised draft
              </Button>
            )}
            {canEdit &&
              !["accepted", "cancelled", "void"].includes(row.status) &&
              row.contract_status !== "completed" && (
                <Button
                  variant="plain"
                  color="danger"
                  sx={{ ml: { sm: "auto" } }}
                  onClick={() => {
                    setError("");
                    setCancelOrder(row);
                  }}
                >
                  Cancel change order
                </Button>
              )}
          </Stack>
        </Card>
      ))}
      <Typography level="body-xs">
        Signed change orders record the homeowner's approval. Invoice charges
        are managed in Billing.
      </Typography>
      <Modal open={!!draft} onClose={() => !busy && setDraft(null)}>
        <ModalDialog
          sx={{ width: 880, maxWidth: "calc(100vw - 24px)", overflowY: "auto" }}
        >
          <ModalClose aria-label="Close dialog" disabled={busy} />
          <Typography level="h3">
            {draft?.id ? "Edit change order" : "New change order"}
          </Typography>
          <Typography level="body-sm">
            Describe the change, then add the work and any credits. You can
            review the PDF before sending.
          </Typography>
          {error && <Alert color="danger">{error}</Alert>}
          {draft && (
            <>
              <FormControl required>
                <FormLabel>Reason for change</FormLabel>
                <Textarea
                  minRows={2}
                  placeholder="What changed and why?"
                  value={draft.change_reason}
                  onChange={(e) =>
                    setDraft({ ...draft, change_reason: e.target.value })
                  }
                />
              </FormControl>
              <Stack spacing={1.5}>
                {draft.line_items.map((item, index) => (
                  <Card key={index} variant="soft" sx={{ p: 2 }}>
                    <Stack direction="row" gap={1} alignItems="center">
                      <Typography level="title-sm" sx={{ flex: 1 }}>
                        Scope item {index + 1}
                      </Typography>
                      <Button
                        size="sm"
                        variant="plain"
                        color="danger"
                        aria-label={`Remove scope item ${index + 1}`}
                        disabled={draft.line_items.length === 1}
                        onClick={() =>
                          setDraft({
                            ...draft,
                            line_items: draft.line_items.filter(
                              (_, i) => i !== index,
                            ),
                          })
                        }
                      >
                        <Trash />
                      </Button>
                    </Stack>
                    <FormControl required>
                      <FormLabel>Description</FormLabel>
                      <Textarea
                        minRows={2}
                        value={item.description}
                        placeholder="Describe the added or removed work"
                        onChange={(e) =>
                          setDraft({
                            ...draft,
                            line_items: draft.line_items.map((line, i) =>
                              i === index
                                ? { ...line, description: e.target.value }
                                : line,
                            ),
                          })
                        }
                      />
                    </FormControl>
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      gap={2}
                      alignItems={{ sm: "end" }}
                    >
                      {(["quantity", "unit_price"] as const).map((key) => (
                        <FormControl key={key} sx={{ flex: 1 }}>
                          <FormLabel>
                            {key === "quantity"
                              ? "Quantity"
                              : "Unit price / credit"}
                          </FormLabel>
                          <Input
                            type="number"
                            value={item[key]}
                            startDecorator={
                              key === "unit_price" ? "$" : undefined
                            }
                            slotProps={{
                              input: {
                                step: key === "quantity" ? 0.001 : 0.01,
                                ...(key === "quantity" ? { min: 0.001 } : {}),
                              },
                            }}
                            onChange={(e) =>
                              setDraft({
                                ...draft,
                                line_items: draft.line_items.map((line, i) =>
                                  i === index
                                    ? { ...line, [key]: e.target.value }
                                    : line,
                                ),
                              })
                            }
                          />
                        </FormControl>
                      ))}
                      <Typography level="title-lg" sx={{ py: 1 }}>
                        {dollars(
                          Number(item.quantity) * Number(item.unit_price),
                        )}
                      </Typography>
                    </Stack>
                    <Checkbox
                      label="Taxable at the organization's default rate"
                      checked={item.is_taxable}
                      onChange={(e) =>
                        setDraft({
                          ...draft,
                          line_items: draft.line_items.map((line, i) =>
                            i === index
                              ? { ...line, is_taxable: e.target.checked }
                              : line,
                          ),
                        })
                      }
                    />
                  </Card>
                ))}
              </Stack>
              <Stack
                direction="row"
                justifyContent="space-between"
                gap={2}
                alignItems="center"
              >
                <Button
                  variant="outlined"
                  startDecorator={<Plus />}
                  onClick={() =>
                    setDraft({
                      ...draft,
                      line_items: [...draft.line_items, blankLine()],
                    })
                  }
                >
                  Add scope item
                </Button>
                <Typography level="title-lg">
                  Subtotal{" "}
                  {dollars(
                    draft.line_items.reduce(
                      (sum, i) =>
                        sum + Number(i.quantity) * Number(i.unit_price),
                      0,
                    ),
                  )}
                </Typography>
              </Stack>
              <Typography level="body-xs">
                Use a negative unit price for credits. Applicable tax is
                calculated when the draft is saved.
              </Typography>
              <FormControl>
                <FormLabel>Homeowner notes</FormLabel>
                <Textarea
                  minRows={2}
                  value={draft.notes}
                  onChange={(e) =>
                    setDraft({ ...draft, notes: e.target.value })
                  }
                />
              </FormControl>
              <FormControl>
                <FormLabel>Terms for this change</FormLabel>
                <Textarea
                  minRows={2}
                  value={draft.terms_and_conditions}
                  onChange={(e) =>
                    setDraft({ ...draft, terms_and_conditions: e.target.value })
                  }
                />
              </FormControl>
              <Stack
                direction="row"
                justifyContent="flex-end"
                gap={1}
                sx={{
                  position: "sticky",
                  bottom: -24,
                  bgcolor: "background.surface",
                  borderTop: "1px solid",
                  borderColor: "divider",
                  py: 2,
                  zIndex: 1,
                }}
              >
                <Button
                  variant="plain"
                  disabled={busy}
                  onClick={() => setDraft(null)}
                >
                  Cancel
                </Button>
                <Button
                  loading={busy}
                  disabled={
                    !(draft.id ? canEdit : canCreate) ||
                    !draft.change_reason.trim() ||
                    draft.line_items.some(
                      (i) =>
                        !i.description.trim() ||
                        !(Number(i.quantity) > 0) ||
                        i.unit_price === "",
                    )
                  }
                  onClick={() =>
                    void act(async () => {
                      const data = { ...draft, job: jobId };
                      if (draft.id)
                        await apiClient.patch(
                          `/api/job-change-orders/${draft.id}/`,
                          data,
                        );
                      else
                        await apiClient.post("/api/job-change-orders/", data);
                      setDraft(null);
                    })
                  }
                >
                  Save draft
                </Button>
              </Stack>
            </>
          )}
        </ModalDialog>
      </Modal>
      <Modal open={!!sending} onClose={() => !busy && setSending(null)}>
        <ModalDialog
          sx={{
            width: 600,
            maxWidth: "calc(100vw - 24px)",
            maxHeight: "calc(100dvh - 32px)",
            overflowY: "auto",
          }}
        >
          <ModalClose aria-label="Close dialog" disabled={busy} />
          <Typography level="h3">Send for signature</Typography>
          <Typography>
            {sending?.document_number} · {sending?.customer_name}
          </Typography>
          <Alert color="neutral">
            The scope and price are now locked. The homeowner signs the prepared
            PDF; the job owner is notified when signing is complete.
          </Alert>
          {error && <Alert color="danger">{error}</Alert>}
          <Typography level="body-sm">
            Review the homeowner’s delivery details. Changes apply to this
            invitation and do not update the contact.
          </Typography>
          <Card variant="outlined" size="sm">
            <Checkbox
              label="Send by email"
              checked={email}
              disabled={!!results || busy}
              onChange={(e) => setEmail(e.target.checked)}
            />
            <FormControl required>
              <FormLabel>Homeowner email</FormLabel>
              <Input
                type="email"
                value={recipientEmail}
                disabled={!!results || busy}
                placeholder="name@example.com"
                onChange={(e) => setRecipientEmail(e.target.value)}
              />
            </FormControl>
            {!email && (
              <Typography level="body-xs">
                Identifies the homeowner signing the document. No email will be
                sent.
              </Typography>
            )}
          </Card>
          <Card variant="outlined" size="sm">
            <Checkbox
              label="Send by text message"
              checked={sms}
              disabled={!!results || busy}
              onChange={(e) => setSms(e.target.checked)}
            />
            {sms && (
              <>
                <FormControl required>
                  <FormLabel>Phone number</FormLabel>
                  <Input
                    type="tel"
                    value={recipientPhone}
                    disabled={!!results || busy}
                    placeholder="(555) 123-4567"
                    onChange={(e) => setRecipientPhone(e.target.value)}
                  />
                </FormControl>
                <Typography level="body-xs">
                  Uses your business texting number. Texting consent is required
                  for the number entered.
                </Typography>
              </>
            )}
          </Card>
          <FormControl>
            <FormLabel>Message</FormLabel>
            <Textarea
              minRows={3}
              value={note}
              disabled={!!results || busy}
              onChange={(e) => setNote(e.target.value)}
            />
          </FormControl>
          {results &&
            Object.entries(results).map(([channel, result]) => (
              <Alert
                key={channel}
                color={successful(result.status) ? "success" : "warning"}
              >
                {channel === "sms" ? "Text message" : "Email"}:{" "}
                {channel === "email" &&
                ["accepted", "sent"].includes(result.status)
                  ? "Accepted by email provider"
                  : result.status.replaceAll("_", " ")}
                {result.recipient && ` · ${result.recipient}`}
                {result.error ? ` — ${result.error}` : ""}
              </Alert>
            ))}
          {results?.email && successful(results.email.status) && (
            <Typography level="body-xs">
              The email provider accepted this invitation. Inbox delivery has
              not been confirmed.
            </Typography>
          )}
          <Stack direction="row" gap={1} justifyContent="flex-end">
            <Button
              variant="plain"
              disabled={busy}
              onClick={() => setSending(null)}
            >
              {results ? "Done" : "Close"}
            </Button>
            {!results && (
              <Button
                loading={busy}
                disabled={
                  (!email && !sms) ||
                  !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recipientEmail.trim()) ||
                  (sms && !recipientPhone.trim())
                }
                startDecorator={<EnvelopeSimple />}
                onClick={() =>
                  void act(async () => {
                    const response = await apiClient.post<{
                      results: Record<string, DeliveryResult>;
                    }>(`/api/job-change-orders/${sending!.id}/send/`, {
                      request_id: sendKey,
                      email,
                      sms,
                      message: note,
                      recipient_email: recipientEmail.trim(),
                      recipient_phone: recipientPhone.trim(),
                    });
                    setResults(response.data.results);
                  })
                }
              >
                Send change order
              </Button>
            )}
          </Stack>
        </ModalDialog>
      </Modal>
      <Modal open={!!cancelOrder} onClose={() => !busy && setCancelOrder(null)}>
        <ModalDialog>
          <Typography level="h4">
            Cancel {cancelOrder?.document_number}?
          </Typography>
          <Typography>
            Its signing links will stop working. You can create a revised draft
            afterwards.
          </Typography>
          {error && <Alert color="danger">{error}</Alert>}
          <Stack direction="row" gap={1} justifyContent="flex-end">
            <Button variant="plain" onClick={() => setCancelOrder(null)}>
              Keep change order
            </Button>
            <Button
              color="danger"
              loading={busy}
              onClick={() =>
                void act(async () => {
                  await apiClient.post(
                    `/api/job-change-orders/${cancelOrder!.id}/cancel/`,
                    {},
                  );
                  setCancelOrder(null);
                })
              }
            >
              Cancel change order
            </Button>
          </Stack>
        </ModalDialog>
      </Modal>
      {preview && (
        <PdfViewerModal
          open
          pdfUrl={`/api/job-change-orders/${preview.id}/pdf/`}
          title={`${preview.document_number} · ${preview.signed_document ? "Signed change order" : "Change order"}`}
          onClose={() => setPreview(null)}
        />
      )}
    </Stack>
  );
}
